import { Box, Divider } from "@mui/joy";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

import { useProject } from "../../../../hooks/query";
import { IProjectList } from "../../../../interfaces";

import { Title } from "./Title";
import { StatusGraph } from "./StatusGraph";
import { ProjectListSkeleton } from "./Skeleton";
import { Alert } from "../../../../shared";
import { DataGrid } from "../../..";

import { columnDef } from "./column-def";
import { MobileProjectList } from "./MobileProjectList";

export default function ListProject() {
  const { useList } = useProject();
  const projectListSvc = useList();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 800);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const projects = useMemo(() => {
    const data = (projectListSvc.data as IProjectList[]) || [];
    return [...data].sort(
      (a: any, b: any) =>
        new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime(),
    );
  }, [projectListSvc.data]);

  const handleRowClicked = (event: any) => {
    navigate(`/project/list/${event.data.Id}`);
  };

  const isLoading = projectListSvc.isPending && projects.length === 0;

  return (
    <Box
      flex={1}
      display={"flex"}
      flexDirection={"column"}
      position={"relative"}
    >
      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <ProjectListSkeleton />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            style={{
              width: "100%",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box mx={{ xs: 2, md: 3 }} mt={2}>
              <Title onRefreshClick={() => projectListSvc.refetch()} svc={projectListSvc} />
            </Box>

            {projectListSvc.error && (
              <Box mx={{ xs: 2, md: 3 }} my={3}>
                <Alert errorMessage={projectListSvc.error} />
              </Box>
            )}

            <Divider sx={{ mt: 2 }} />
            <Box mx={{ xs: 2, md: 3 }} mt={2}>
              <StatusGraph />
            </Box>
            <Divider sx={{ mt: 1.5 }} />
            <Box flex={1}>
              {projects.length > 0 && (
                <>
                  {isMobile ? (
                    <MobileProjectList
                      projects={projects}
                      onProjectClick={(id) => navigate(`/project/list/${id}`)}
                    />
                  ) : (
                    <DataGrid
                      gridData={projects}
                      columnDefs={columnDef}
                      hideHeader={true}
                      suppressCellFocus={true}
                      onRowClicked={handleRowClicked}
                      searchInputSx={{ pl: 1 }}
                      rowHeight={60}
                      rowStyle={{
                        cursor: "pointer",
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
}
